import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  WarningOutlined
} from "@ant-design/icons"
import { Flex, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import Icon from "../../Icon"

const { Title, Text } = Typography

// Analytics-tab-only (`AnalyticsKpiTrend | undefined` per card) — populated
// from `AnalyticsTotals`'s own `changePercent`/`changeLabel` once the
// Analytics tab has fetched at least once this session (`MapTab.tsx` reads
// the plain Redux selector, not the fetching hook, so this stays populated
// even after navigating back to Map/List). `undefined` — on Map/List, or
// before Analytics has loaded — renders exactly as before: no trend line.
interface KpiTrend {
  changePercent: number | null
  changeLabel: "new" | null
}

interface GlobalMapHeaderProps {
  // Entities- and filter-aware totals (`data.project_total`/`lead_total`/
  // `subcontractor_total`/`appointment_total`) — each zeroes out when its
  // own layer toggle excludes it (2026-09-19 reversal of the original
  // "unconditional of `entities`" contract). `undefined` before the first
  // fetch resolves, in which case the real cards show a loading dash rather
  // than a misleading `0`.
  projectTotal?: number
  leadTotal?: number
  subcontractorTotal?: number
  appointmentTotal?: number
  // Analytics tab only — see `KpiTrend`'s own doc comment above.
  trends?: {
    leads?: KpiTrend
    projects?: KpiTrend
    subcontractors?: KpiTrend
    appointments?: KpiTrend
  }
}

interface KpiCardSpec {
  key: string
  icon: React.ReactNode
  color: string
  label: string
  count: number | undefined
  // Set only for cards with no real data source yet (Workforce) — see the
  // component doc comment below for why "—" alone wasn't enough of a signal.
  tooltip?: string
  trend?: KpiTrend
  // Leads/Projects/Subcontractors/Appointments: an increase is positive
  // (green). At Risk SLA is the one inverted card (spec: "Increase is
  // negative; decrease is positive") — plumbed per-card since it's the only
  // exception, rather than a blanket "higher is better" assumption.
  invertTrendColor?: boolean
}

// `((current - previous) / previous) * 100`, already computed server-side —
// this only decides how to DISPLAY it: "New" when there was no previous
// period to compare against (spec: never a fabricated ∞%), a signed
// percentage otherwise. `invert` flips which direction reads as "good"
// (green) — At Risk SLA only.
function TrendBadge({ trend, invert }: { trend: KpiTrend; invert?: boolean }) {
  const { t } = useTranslation()
  if (trend.changeLabel === "new") {
    return (
      <span className="text-xs font-medium text-slate-400">
        {t("globalMap.kpi.trendNew")}
      </span>
    )
  }
  if (trend.changePercent === null) return null

  const isIncrease = trend.changePercent > 0
  const isFlat = trend.changePercent === 0
  const isGood = isFlat ? null : invert ? !isIncrease : isIncrease
  const colorClass = isFlat
    ? "text-slate-400"
    : isGood
      ? "text-emerald-600"
      : "text-red-600"

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${colorClass}`}
    >
      {!isFlat && (isIncrease ? <ArrowUpOutlined /> : <ArrowDownOutlined />)}
      {Math.abs(trend.changePercent)}%
    </span>
  )
}

/**
 * Title + KPI card row for the Global Map surface, restyled 2026-09-19
 * against the client's own `Target Implementation.png` /
 * `Current Implementation.png` pair (read via real file paths) — moved the
 * KPI cards from the tab-bar row (an earlier, image-less round's guess at
 * "move it to the Map/List/Analytics line") back to sitting beside the
 * title, matching this concrete reference exactly.
 *
 * The Map/List/Analytics tab bar itself moved out to `GlobalMapToolbar`
 * (rendered separately by `MapTab`, combined with the filter bar on one
 * line) in the same 2026-09-19 corrective pass — see that file's own
 * docstring — so this component now owns only the title/subtitle/KPI row.
 *
 * Six cards total, matching the reference's own entity set and distinct
 * per-entity colors (blue/green/orange/purple/teal/red) — deliberately NOT
 * the map markers' own blue-project/teal-lead grammar, since the task
 * explicitly asks for this six-color palette here and the marker grammar is
 * "inside the map," out of scope. Leads/Projects/Subcontractors/Appointments
 * all show a real count (`data.lead_total`/`project_total`/
 * `subcontractor_total`/`appointment_total` — the backend's own
 * `MapFeaturesView.get`; all four are filter-aware AND entities-aware, so
 * toggling a layer off zeroes its tile too, per explicit live product
 * feedback that a frozen tile beside a toggled-off layer read as broken).
 * Workforce/At-Risk SLA still have no count wired into Global Map's own data
 * flow, so those two cards show a plain "—" rather than a fabricated number,
 * and both carry the same "Coming soon" tooltip per explicit product
 * direction. The other four cards render a real change-percentage line (the
 * reference's "↑12%" etc.) ONLY on the Analytics tab, once the Analytics
 * endpoint's own real period-over-period comparison has loaded (`trends`
 * prop) — Map/List never had that data and still render exactly as before.
 * Neither Subcontractors nor Appointments is clickable (an earlier round of
 * this same pass explored filtering the map by clicking the Appointments
 * tile — explicitly descoped by product direction to "just show the data,"
 * so both tiles are plain display, matching Leads/Projects' own
 * non-interactive cards).
 */
const GlobalMapHeader = ({
  projectTotal,
  leadTotal,
  subcontractorTotal,
  appointmentTotal,
  trends
}: GlobalMapHeaderProps) => {
  const { t } = useTranslation()

  const kpiCards: KpiCardSpec[] = [
    {
      key: "leads",
      icon: <Icon type="Leads" />,
      color: "#1677ff",
      label: t("globalMap.kpi.leadsLabel"),
      count: leadTotal,
      trend: trends?.leads
    },
    {
      key: "projects",
      icon: <Icon type="Projects" />,
      color: "#16a34a",
      label: t("globalMap.kpi.projectsLabel"),
      count: projectTotal,
      trend: trends?.projects
    },
    {
      key: "subcontractors",
      icon: <Icon type="Subcontractors" />,
      color: "#9333ea",
      label: t("globalMap.kpi.subcontractorsLabel"),
      count: subcontractorTotal,
      trend: trends?.subcontractors
    },
    {
      key: "appointments",
      icon: <Icon type="Calendar" />,
      color: "#0ea5e9",
      label: t("globalMap.kpi.appointmentsLabel"),
      count: appointmentTotal,
      trend: trends?.appointments
    },
    {
      key: "workforce",
      icon: <Icon type="User" />,
      color: "#f97316",
      label: t("globalMap.kpi.workforceLabel"),
      count: undefined,
      tooltip: t("globalMap.tabs.comingSoon")
    },
    {
      key: "atRiskSla",
      icon: <WarningOutlined />,
      color: "#dc2626",
      label: t("globalMap.kpi.atRiskSlaLabel"),
      count: undefined,
      tooltip: t("globalMap.tabs.comingSoon"),
      invertTrendColor: true
    }
  ]

  return (
    <div className="grid shrink-0 grid-cols-1 items-stretch gap-3 min-[1280px]:grid-cols-[minmax(220px,344px)_minmax(0,1fr)]">
      <Flex vertical gap={2} justify="center" className="shrink-0">
        {/* app.less sets a global `h1,h2,h3{text-align:center}` reset — Title
            renders as an h3, so it needs an explicit override here. */}
        <Title level={3} className="m-0 text-left text-[26px] font-semibold">
          {t("globalMap.header.title")}
        </Title>
        <Text type="secondary" className="text-xs">
          {t("globalMap.header.subtitle")}
        </Text>
      </Flex>

      {/* A real 6-column grid (`repeat(6, minmax(0,1fr))`), not a `justify:
          space-between` row — a wrapping flex row left a wide gap between
          the last card and the column's own right edge at desktop widths
          instead of the cards forming one compact block flush with it. */}
      <div
        className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 min-[1280px]:grid-cols-6"
        data-testid="global-map-kpi-row"
      >
        {kpiCards.map((card) => {
          const cardBody = (
            <div
              className="flex min-w-0 items-center gap-2.5 rounded-lg border border-[#E3E8EF] bg-white px-3 py-2"
              data-testid={`global-map-kpi-${card.key}`}
            >
              <span
                aria-hidden="true"
                className="flex size-8 shrink-0 items-center justify-center rounded-md text-white"
                style={{ backgroundColor: card.color }}
              >
                {card.icon}
              </span>
              <div className="min-w-0 leading-tight">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-base font-bold text-gray-900">
                    {card.count ?? "—"}
                  </span>
                  {card.trend && (
                    <TrendBadge
                      trend={card.trend}
                      invert={card.invertTrendColor}
                    />
                  )}
                </div>
                <div className="truncate text-xs text-slate-500">
                  {card.label}
                </div>
              </div>
            </div>
          )

          return card.tooltip ? (
            <Tooltip key={card.key} title={card.tooltip}>
              {cardBody}
            </Tooltip>
          ) : (
            cardBody
          )
        })}
      </div>
    </div>
  )
}

export default GlobalMapHeader
